import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NumberFormatterService } from '../../../../../core/services/number.formatter.service';
import { map, startWith } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { User } from 'anchor-shared/lib/model/entity/user';
import { CharterDAO } from '../../../../../core/services/dao/charterDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { PayoutDAO } from '../../../../../core/services/dao/payoutDAO';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import * as moment from 'moment';
import {
  AdditionalCosts,
  IndividualCost,
} from '../../../../../core/models/interface/additional-cost';
import * as uuid from 'uuid';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { Charter } from 'anchor-shared/lib/model/entity/charter';

@Component({
  selector: 'app-add-new-cost-dialog',
  templateUrl: './add-new-cost-dialog.component.html',
  styleUrls: ['./add-new-cost-dialog.component.scss'],
})
export class AddNewCostDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  charter: Charter;
  currencyMask: any;

  payee: any;
  costTotal: string;
  payeePay: string;
  costDescription: string;
  payeeId: number;
  payeeName: string;

  costPaid: boolean = false;
  userOptions: any;
  filteredUsers: Observable<User[]>;
  autoControl: FormControl = new FormControl();

  costForm: FormGroup;
  manualUserForm: FormGroup;
  payeePaypalEmail: string;
  contactEmail: string;
  companyName: string;
  paymentNetwork: string;
  costToEdit: any;
  subs: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<AddNewCostDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private numFormatter: NumberFormatterService,
    private userDao: UserDAO,
    private charterDao: CharterDAO,
    private payoutDao: PayoutDAO,
  ) {
    this.charter = this.data.charter;
    this.costToEdit = this.data.costToEdit;
    this.costPaid = this.data.costPaid;

    if (this.costToEdit) {
      this.costDescription = this.costToEdit.description;
      this.costTotal = this.costToEdit.total;
      this.payeePay = this.costToEdit.payeePay;
      this.payee = this.costToEdit.payee;
      this.paymentNetwork = this.costToEdit.paymentNetwork;
      this.payeeId = this.costToEdit.userId;
      this.autoControl.setValue(this.costToEdit.fullName);
      this.payeeName = this.costToEdit.fullName;
      this.payeePaypalEmail = this.costToEdit.paypalEmail;
      this.contactEmail = this.costToEdit.contactEmail;
      this.companyName = this.costToEdit.contactEmail;
    } else {
      this.payeePay = '0.00';
      this.costTotal = '0.00';
    }

    this.currencyMask = this.numFormatter.createPriceMask();
    this.costForm = new FormGroup({
      payeePay: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
      ]),
      costTotal: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
      ]),
      description: new FormControl('', [Validators.required]),
      payee: new FormControl('', [Validators.required]),
    });
    this.manualUserForm = new FormGroup({
      payeeName: new FormControl('', [Validators.required]),
      contactEmail: new FormControl('', [
        Validators.required,
        Validators.email,
      ]),
      companyName: new FormControl('', []),
      paymentNetwork: new FormControl('', [Validators.required]),
      payeePaypalEmail: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit() {
    const userSub = this.userDao.getUsersForPayouts().subscribe((data) => {
      this.userOptions = data;
      this.filteredUsers = this.autoControl.valueChanges.pipe(
        startWith(''),
        map((val) => (val && val.length >= 4 ? this._filter(val) : [])),
      );
    });
    this.subs.push(userSub);

    if (this.costPaid) {
      this.costForm.controls.costTotal.disable();
      this.costForm.controls.payeePay.disable();
    } else {
      this.costForm.controls.costTotal.enable();
      this.costForm.controls.payeePay.enable();
    }
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  _filter(val: string): User[] {
    return this.userOptions.filter((s) =>
      new RegExp(val, 'gi').test(s.firstName + ' ' + s.lastName),
    );
  }

  selectUser(event, user: User) {
    if (event.isUserInput) {
      this.payeeId = user.id;
      this.payeePaypalEmail = user.paypalEmail;
      this.payeeName = user.firstName + ' ' + user.lastName;
    }
  }

  save() {
    if (this.costToEdit) {
      this.saveEdits();
    } else {
      let additionalCosts;
      if (this.charter.additionalCosts) {
        typeof this.charter.additionalCosts === 'object'
          ? (additionalCosts = this.charter.additionalCosts)
          : (additionalCosts = this.numFormatter.parseArrayFromJson(
              this.charter.additionalCosts,
            ));
      } else {
        additionalCosts = {
          fuelCosts: {
            gallons: null,
            cost: '0.00',
            costPerGal: null,
          },
          additionalCharges: {
            cost: '0.00',
            individualCosts: [],
          },
          total: '0.00',
        };
      }
      this.payeePay = this.numFormatter
        .unMaskPrice(this.payeePay)
        .toFixed(2)
        .toString();
      this.costTotal = this.numFormatter
        .unMaskPrice(this.costTotal)
        .toFixed(2)
        .toString();

      const payoutPayeeType = this.getPayoutPayeeType();

      let newCost: IndividualCost = {
        description: this.costDescription,
        total: this.costTotal,
        payeePay: this.payeePay,
        payee: this.payee,
        userId: this.payeeId,
        fullName: this.payeeName,
        paypalEmail: this.payeePaypalEmail,
        contactEmail: this.contactEmail,
        companyName: this.companyName,
        paymentNetwork: this.paymentNetwork,
        createdAt: moment().toDate(),
        charterPaidAtCreation: this.charter.paymentReceived,
        charterCompleteAtCreation: this.charter.completed,
        payoutInfo: {
          payoutPayeeUserId: this.payeeId,
          payoutAmount: parseFloat(this.payeePay),
          payoutPayeeType: payoutPayeeType,
          payoutId: Date.now(),
        },
        uuid: uuid.v4(),
      };

      additionalCosts.additionalCharges.individualCosts.push(newCost);

      const costVesselRate: Partial<VesselRate> = {
        name: this.costDescription,
        rateType: RateType.Custom,
        ratePayee: this.payee,
        customRate: parseFloat(this.costTotal),
        vesselId: this.charter.vesselId,
        payToUserId: this.payee === 'Specific User' ? this.payeeId : null,
        markupPercentage: 0,
      };

      this.charter.originalRates.push(costVesselRate as VesselRate);

      additionalCosts.additionalCharges.cost = (
        parseFloat(additionalCosts.additionalCharges.cost) +
        parseFloat(this.costTotal)
      )
        .toFixed(2)
        .toString();
      const charterSub = this.charterDao
        .updateFromPartial(this.charter.id, {
          additionalCosts: additionalCosts,
          originalRates: this.charter.originalRates,
        })
        .subscribe((updated) => {
          this.dialogRef.close(additionalCosts);
        });
      this.subs.push(charterSub);
    }
  }

  close() {
    this.dialogRef.close(null);
  }

  selectPayee(event, payee: string) {
    this.payee = payee;
    if (this.payee === 'Boat Owner') {
      let boatOwnerUser = this.charter.boat.owner.user;
      this.payeeId = boatOwnerUser.id;
      this.payeeName = boatOwnerUser.firstName + ' ' + boatOwnerUser.lastName;
      this.payeePaypalEmail = boatOwnerUser.paypalEmail;
    } else if (this.payee === 'Captain') {
      let captainUser = this.charter.captain?.user;
      if (captainUser) {
        this.payeeId = captainUser.id;
        this.payeeName = captainUser.firstName + ' ' + captainUser.lastName;
        this.payeePaypalEmail = captainUser.paypalEmail;
      }
    } else if (this.payee === 'Anchor') {
      this.payeeName = 'Anchor';
    }
  }

  saveEdits() {
    let additionalCosts: AdditionalCosts;
    additionalCosts = this.numFormatter.parseJsonIfString(
      this.charter.additionalCosts,
    );
    const editCostIndex =
      additionalCosts.additionalCharges.individualCosts.findIndex((cost) => {
        if (this.costToEdit.uuid) {
          return cost.uuid === this.costToEdit.uuid;
        } else {
          return cost.description === this.costToEdit.description;
        }
      });

    this.payeePay = this.numFormatter
      .unMaskPrice(this.payeePay)
      .toFixed(2)
      .toString();
    this.costTotal = this.numFormatter
      .unMaskPrice(this.costTotal)
      .toFixed(2)
      .toString();
    let editedCost = {
      uuid: this.costToEdit.uuid ? this.costToEdit.uuid : null,
      description: this.costDescription,
      total: this.costTotal,
      payeePay: this.payeePay,
      payee: this.payee,
      userId: this.payeeId,
      fullName: this.payeeName,
      paypalEmail: this.payeePaypalEmail,
      contactEmail: this.contactEmail,
      companyName: this.companyName,
      paymentNetwork: this.paymentNetwork,
      charterPaidAtCreation: true,
      charterCompleteAtCreation: true,
      payoutInfo: null,
    };
    if (this.costToEdit.payoutInfo) {
      this.costToEdit.payoutInfo = {
        payoutAmount: this.payeePay,
        payoutPayeeType: this.getPayoutPayeeType(),
        payoutPayeeUserId: this.payeeId,
      };
      editedCost.payoutInfo = this.costToEdit.payoutInfo;
    }

    additionalCosts.additionalCharges.individualCosts[editCostIndex] =
      editedCost;
    let newTotal = 0;
    additionalCosts.additionalCharges.individualCosts.forEach((cost) => {
      newTotal += parseFloat(cost.total);
    });
    additionalCosts.additionalCharges.cost = newTotal;
    const stringedCosts = JSON.stringify(additionalCosts);
    const charterSub = this.charterDao
      .updateFromPartial(this.charter.id, {
        additionalCosts: stringedCosts,
      })
      .subscribe((data) => {
        const payout = this.charter.payouts.find((payout) => {
          return payout.name === this.costToEdit.description;
        });
        if (payout) {
          const editedPayout: Payout = {
            id: payout.id,
            basePay: editedCost.payeePay,
            actualPay: editedCost.payeePay,
            payee: editedCost.fullName,
            payeePaypalHandle: editedCost.paypalEmail,
            type: 'misc',
            name: editedCost.description,
            charterId: this.charter.id,
            paid: false,
            // @ts-ignore
            charterDate: this.charter.date,
          };
          const payoutSub = this.payoutDao
            .updateFromPartial(payout.id, editedPayout)
            .subscribe((payoutData) => {
              const payoutIndex = this.charter.payouts.findIndex((payout) => {
                return payout.name === this.costToEdit.description;
              });
              // @ts-ignore
              this.charter.payouts[payoutIndex] = editedPayout;
            });
          this.subs.push(payoutSub);
        }
      });
    this.subs.push(charterSub);
    this.dialogRef.close([additionalCosts, this.charter.payouts]);
  }

  getPayoutPayeeType(): string {
    if (this.payee === 'Boat Owner') {
      return 'owner';
    } else {
      return this.payee.toLowerCase();
    }
  }
}
